import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { AppRoutes } from '@app/routes/constants/appRoutes';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { LocalStorage } from '../enum/localStorage.enum';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { CartChangeService } from './cart-change.service';
import { filter, take } from 'rxjs';

interface IPendingAction {
  action: string;
  data: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private authDrawerSubject = new BehaviorSubject<boolean>(false);
  private nameSubject = new BehaviorSubject<string>('');
  private pendingActionSubject = new BehaviorSubject<IPendingAction | null>(null);
  public redirectToCartSubject = new BehaviorSubject<boolean>(false);
  private isInitialized = false;

  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public authDrawer$ = this.authDrawerSubject.asObservable();
  public name$ = this.nameSubject.asObservable();
  public pendingAction$ = this.pendingActionSubject.asObservable();
  public redirectToCart$ = this.redirectToCartSubject.asObservable();

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object,
    private cartChangeService: CartChangeService
  ) {
    this.initializeAuth();
    this.setupAuthListener();
  }

  setName(name: string) {
    this.nameSubject.next(name);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(LocalStorage.NAME, name);
    }
  }

  getName(): string {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem(LocalStorage.NAME) || '';
    }
    return '';
  }

  /**
   * Initialize authentication state with SSR support
   * On server: always returns false
   * On client: checks localStorage and initializes properly
   */
  private initializeAuth(): void {
    if (isPlatformServer(this.platformId)) {
      // On server, always start as unauthenticated
      this.isAuthenticatedSubject.next(false);
      this.isInitialized = true;
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      // On client, check localStorage and initialize
      const isAuthenticated = this.checkInitialAuthStatus();
      this.isAuthenticatedSubject.next(isAuthenticated);

      // Load user name if authenticated
      if (isAuthenticated) {
        const name = this.getName();
        if (name) {
          this.nameSubject.next(name);
        }
      }

      this.isInitialized = true;
    }
  }

  private checkInitialAuthStatus(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const accessToken = localStorage.getItem(LocalStorage.COMMERCIAL_ACCESS_TOKEN);
    return !!accessToken;
  }

  private setupAuthListener() {
    if (isPlatformBrowser(this.platformId) && window) {
      window.addEventListener('storage', (event) => {
        if (event.key === LocalStorage.ACCESS_TOKEN) {
          const isAuthenticated = !!event.newValue;
          this.isAuthenticatedSubject.next(isAuthenticated);
        }
      });
    }
  }

  public setAuthStatus(isAuthenticated: boolean) {
    this.isAuthenticatedSubject.next(isAuthenticated);
  }

  public updateAuthStatus(isAuthenticated: boolean) {
    this.setAuthStatus(isAuthenticated);

    if (isAuthenticated) {
      this.closeAuthDrawer();
      // Execute any pending action after successful login
      this.executePendingAction();
    } else {
      this.clearLocalStorage();
    }
  }

  public clearLocalStorage() {
    if (!isPlatformBrowser(this.platformId)) return;

    Object.values(LocalStorage)
      .filter((value) => !value.includes('app-storage-state'))
      .forEach((value) => localStorage.removeItem(value));
  }

  public logout() {
    this.updateAuthStatus(false);

    // Wait for isAuthenticated$ to emit false, then notify cart change
    this.isAuthenticated$
      .pipe(
        filter((isAuth) => isAuth === false),
        take(1)
      )
      .subscribe(() => {
        this.cartChangeService.notifyCartChanged(null);
      });

    this.router.navigate([AppRoutes.ROOT]);
  }

  public openAuthDrawer() {
    this.authDrawerSubject.next(true);
  }

  public closeAuthDrawer() {
    this.authDrawerSubject.next(false);
    // Clear pending action if drawer is closed without successful login
    if (!this.isAuthenticatedSubject.value) {
      this.clearPendingAction();
    }
  }

  public setPendingAction(action: string, data: unknown) {
    this.pendingActionSubject.next({ action, data });
  }

  public clearPendingAction() {
    this.pendingActionSubject.next(null);
  }

  public getPendingAction(): IPendingAction | null {
    return this.pendingActionSubject.value;
  }

  /**
   * Get current authentication status
   * Safe for SSR - returns false on server
   */
  public getCurrentAuthStatus(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  /**
   * Check if authentication service is properly initialized
   */
  public isAuthInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Get authentication state that's safe for SSR
   * Returns false on server, actual state on client after initialization
   */
  public getSSRSafeAuthState(): Observable<boolean> {
    if (isPlatformServer(this.platformId)) {
      return of(false);
    }

    if (isPlatformBrowser(this.platformId)) {
      // If already initialized, return current state immediately
      if (this.isInitialized) {
        return of(this.isAuthenticatedSubject.value);
      }

      // If not initialized, wait for initialization then return state
      return this.isAuthenticated$.pipe(
        filter(() => this.isInitialized),
        take(1)
      );
    }

    return of(false);
  }

  private executePendingAction() {
    const pendingAction = this.getPendingAction();
    if (pendingAction) {
      // Emit the pending action so components can listen and handle it
      // We'll use a small delay to ensure the auth state is fully updated
      setTimeout(() => {
        this.pendingActionSubject.next(pendingAction);
        // Clear the pending action after execution
        setTimeout(() => this.clearPendingAction(), 100);
      }, 50);
    }
  }
}
